import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Clock, Users, Car, Calendar, Repeat, HandHelping, User } from "lucide-react";
import ModePicker from "@/components/ModePicker";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const DOW_FULL = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

function todayISO() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}
function isoWeekday(isoDate) {
  const d = new Date(isoDate + "T00:00:00");
  return (d.getDay() + 6) % 7;
}
function addDaysISO(isoDate, days) {
  const d = new Date(isoDate + "T00:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function NewRide() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const TODAY = todayISO();
  const initialKind = params.get("type") === "request" ? "request" : "offer";
  const initialDate = (() => {
    const d = params.get("date");
    return d && /^\d{4}-\d{2}-\d{2}$/.test(d) && d >= TODAY ? d : TODAY;
  })();
  const [kind, setKind] = useState(initialKind); // 'offer' | 'request'
  const [date, setDate] = useState(initialDate);
  const [time, setTime] = useState("08:00");
  const [destination, setDestination] = useState("");
  const [destinationAddress, setDestinationAddress] = useState("");
  const [origin, setOrigin] = useState("");
  const [originAddress, setOriginAddress] = useState("");
  const [seats, setSeats] = useState(3);
  const [notes, setNotes] = useState("");
  const [childName, setChildName] = useState("");
  const [mode, setMode] = useState("car");
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [places, setPlaces] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/places");
        setPlaces(data.places || []);
      } catch {
        /* ignore - places are optional */
      }
    })();
  }, []);

  // Auto-fill address when name matches a saved place
  useEffect(() => {
    if (!destination || destinationAddress) return;
    const match = places.find((p) => p.label.trim().toLowerCase() === destination.trim().toLowerCase());
    if (match) setDestinationAddress(match.address);
  }, [destination, places, destinationAddress]);
  useEffect(() => {
    if (!origin || originAddress) return;
    const match = places.find((p) => p.label.trim().toLowerCase() === origin.trim().toLowerCase());
    if (match) setOriginAddress(match.address);
  }, [origin, places, originAddress]);

  const applyPlace = (which, p) => {
    if (which === "dest") {
      setDestination(p.label);
      setDestinationAddress(p.address);
    } else {
      setOrigin(p.label);
      setOriginAddress(p.address);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/groups");
        setGroups(data.groups || []);
        const aid = data.active_group_id || (data.groups?.[0]?.id ?? null);
        setActiveGroupId(aid);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setGroupsLoading(false);
      }
    })();
  }, []);

  // Recurrence (only for offer)
  const [recurring, setRecurring] = useState(false);
  const [weekdays, setWeekdays] = useState(() => [isoWeekday(date)]);
  const [untilDate, setUntilDate] = useState(() => addDaysISO(TODAY, 28));

  const previewCount = useMemo(() => {
    if (kind !== "offer" || !recurring) return 1;
    if (!weekdays.length) return 0;
    const start = new Date(date + "T00:00:00");
    const end = new Date(untilDate + "T00:00:00");
    if (end < start) return 0;
    let n = 0;
    const cur = new Date(start);
    while (cur <= end) {
      const wd = (cur.getDay() + 6) % 7;
      if (weekdays.includes(wd)) n++;
      cur.setDate(cur.getDate() + 1);
    }
    return n;
  }, [kind, recurring, date, untilDate, weekdays]);

  const toggleWeekday = (wd) => {
    setWeekdays((prev) => (prev.includes(wd) ? prev.filter((x) => x !== wd) : [...prev, wd].sort()));
  };

  const onDateChange = (newDate) => {
    setDate(newDate);
    if (recurring && weekdays.length === 1) {
      setWeekdays([isoWeekday(newDate)]);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!activeGroupId) {
      toast.error("Seleziona un gruppo");
      return;
    }
    if (date < TODAY) {
      toast.error("Non puoi creare nel passato");
      return;
    }
    setLoading(true);
    try {
      // Make sure server-side active group matches the user's selection before creating
      await api.post(`/groups/switch/${activeGroupId}`);
      if (kind === "request") {
        if (!childName.trim()) {
          toast.error("Inserisci il nome del bambino");
          setLoading(false);
          return;
        }
        const { data } = await api.post("/ride-requests", {
          date, time, destination,
          destination_address: destinationAddress || null,
          origin: origin || null,
          origin_address: originAddress || null,
          child_name: childName,
          notes: notes || null,
          mode,
        });
        toast.success("Richiesta pubblicata!");
        nav(`/rides/${data.id}`);
      } else {
        // offer
        if (recurring) {
          if (weekdays.length === 0) { toast.error("Seleziona almeno un giorno"); setLoading(false); return; }
          if (untilDate < date) { toast.error("Data finale non valida"); setLoading(false); return; }
          if (previewCount === 0) { toast.error("Nessuna data corrisponde"); setLoading(false); return; }
          if (previewCount > 60) { toast.error("Troppe corse (max 60)"); setLoading(false); return; }
        }
        const body = { date, time, destination, destination_address: destinationAddress || null, origin: origin || null, origin_address: originAddress || null, seats: Number(seats), notes: notes || null, mode };
        if (recurring) body.recurrence = { weekdays, until_date: untilDate };
        const { data } = await api.post("/rides", body);
        if (data.created_count && data.created_count > 1) {
          toast.success(`${data.created_count} corse create!`);
          nav("/dashboard");
        } else {
          toast.success("Corsa creata!");
          nav(`/rides/${data.id}`);
        }
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const isOffer = kind === "offer";

  return (
    <div className="max-w-2xl mx-auto anim-slide-up">
      <Link to="/dashboard" className="inline-flex items-center gap-2 mb-4 font-bold text-sm text-[#1A2A6C] hover:underline" data-testid="newride-back">
        <ArrowLeft className="w-4 h-4" /> Indietro
      </Link>

      {/* Segmented control */}
      <div className="grid grid-cols-2 gap-2 mb-5 bg-white rounded-2xl p-1.5 border border-[#ECEEF5] shadow-[0_2px_10px_rgba(20,32,80,0.04)]">
        <button
          type="button"
          onClick={() => setKind("offer")}
          className={`px-3 py-2.5 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 ${isOffer ? "bg-[#1A2A6C] text-white shadow-[0_4px_12px_rgba(26,42,108,0.32)]" : "text-[#8A91A8] hover:bg-[#F5F6FA]"}`}
          data-testid="newride-kind-offer"
        >
          <Car className="w-4 h-4" /> Offro una corsa
        </button>
        <button
          type="button"
          onClick={() => setKind("request")}
          className={`px-3 py-2.5 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 ${!isOffer ? "bg-[#FF9E1B] text-white shadow-[0_4px_12px_rgba(255,158,27,0.32)]" : "text-[#8A91A8] hover:bg-[#F5F6FA]"}`}
          data-testid="newride-kind-request"
        >
          <HandHelping className="w-4 h-4" /> Cerco una corsa
        </button>
      </div>

      <div className="brut-card p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className={`w-12 h-12 grid place-items-center rounded-2xl text-white ${isOffer ? "bg-[#1A2A6C] shadow-[0_6px_18px_rgba(26,42,108,0.32)]" : "bg-[#FF9E1B] shadow-[0_6px_18px_rgba(255,158,27,0.32)]"}`}>
            {isOffer ? <Car className="w-5 h-5" strokeWidth={2.5} /> : <HandHelping className="w-5 h-5" strokeWidth={2.5} />}
          </div>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
              {isOffer ? "Nuova corsa" : "Richiedi una corsa"}
            </h1>
            <p className="text-sm text-[#8A91A8]">
              {isOffer
                ? "Offri una corsa ai bambini del gruppo."
                : "Cerca un genitore che possa portare il tuo bambino."}
            </p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-5">
          <div>
            <label className="brut-label"><Users className="w-3 h-3 inline mr-1" /> Gruppo</label>
            {groupsLoading ? (
              <p className="text-sm text-[#8A91A8]">Caricamento gruppi…</p>
            ) : groups.length === 0 ? (
              <p className="text-sm text-[#FF4D5E]">Non sei in nessun gruppo. <Link to="/onboarding" className="underline font-semibold">Crea o entra in un gruppo</Link>.</p>
            ) : groups.length === 1 ? (
              <div className="brut-input flex items-center" data-testid="newride-group-single">
                <span className="font-semibold">{groups[0].name}</span>
                <span className="ml-auto text-xs text-[#8A91A8]">{groups[0].members_count} {groups[0].members_count === 1 ? "membro" : "membri"}</span>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" data-testid="newride-group-picker">
                {groups.map((g) => {
                  const active = activeGroupId === g.id;
                  return (
                    <button
                      type="button"
                      key={g.id}
                      onClick={() => setActiveGroupId(g.id)}
                      aria-pressed={active}
                      className={`px-3 py-3 rounded-2xl border-2 text-left transition-all ${
                        active ? "border-[#1A2A6C] bg-[#F5F6FA] shadow-[0_4px_12px_rgba(26,42,108,0.12)]" : "border-[#ECEEF5] bg-white hover:bg-[#F8F9FC]"
                      }`}
                      data-testid={`newride-group-${g.id}`}
                    >
                      <p className="font-bold text-sm text-[#1A1D29] truncate">{g.name}</p>
                      <p className="text-xs text-[#8A91A8] truncate">{g.members_count} {g.members_count === 1 ? "membro" : "membri"}</p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="brut-label"><Calendar className="w-3 h-3 inline mr-1" /> Data</label>
              <input type="date" className="brut-input" min={TODAY} value={date} onChange={(e) => onDateChange(e.target.value)} required data-testid="newride-date-input" />
            </div>
            <div>
              <label className="brut-label"><Clock className="w-3 h-3 inline mr-1" /> Ora</label>
              <input type="time" className="brut-input" value={time} onChange={(e) => setTime(e.target.value)} required data-testid="newride-time-input" />
            </div>
          </div>

          <div>
            <label className="brut-label">Modalità di trasporto</label>
            <ModePicker value={mode} onChange={setMode} />
          </div>

          <div>
            <label className="brut-label"><MapPin className="w-3 h-3 inline mr-1" /> Destinazione</label>
            <input type="text" className="brut-input" placeholder="Es. Scuola Primaria Marconi" value={destination} onChange={(e) => setDestination(e.target.value)} required maxLength={200} data-testid="newride-destination-input" />
            {places.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="newride-destination-places">
                {places.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => applyPlace("dest", p)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F5F6FA] text-[#1A2A6C] text-xs font-bold hover:bg-[#E5E9F5]"
                    data-testid={`newride-dest-place-${p.id}`}
                  >
                    <MapPin className="w-3 h-3" /> {p.label}
                  </button>
                ))}
              </div>
            )}
            <div className="mt-2">
              <AddressAutocomplete value={destinationAddress} onChange={setDestinationAddress} placeholder="Cerca via, indirizzo…" testId="newride-destination-address-input" />
            </div>
          </div>

          <div>
            <label className="brut-label">Punto di partenza</label>
            <input type="text" className="brut-input" placeholder="Es. Casa, Ufficio…" value={origin} onChange={(e) => setOrigin(e.target.value)} maxLength={200} data-testid="newride-origin-input" />
            {places.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="newride-origin-places">
                {places.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => applyPlace("orig", p)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#F5F6FA] text-[#1A2A6C] text-xs font-bold hover:bg-[#E5E9F5]"
                    data-testid={`newride-orig-place-${p.id}`}
                  >
                    <MapPin className="w-3 h-3" /> {p.label}
                  </button>
                ))}
              </div>
            )}
            <div className="mt-2">
              <AddressAutocomplete value={originAddress} onChange={setOriginAddress} placeholder="Cerca via, indirizzo…" testId="newride-origin-address-input" />
            </div>
          </div>

          {!isOffer && (
            <div className="anim-slide-up">
              <label className="brut-label"><User className="w-3 h-3 inline mr-1" /> Nome bambino</label>
              <input type="text" className="brut-input" placeholder="Es. Marco" value={childName} onChange={(e) => setChildName(e.target.value)} required={!isOffer} maxLength={80} data-testid="newride-child-name-input" />
              <p className="text-xs text-[#8A91A8] mt-1.5">Il bambino per cui cerchi la corsa.</p>
            </div>
          )}

          {isOffer && (
            <div className="rounded-2xl border border-[#ECEEF5] bg-white p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" className="mt-1 w-5 h-5 accent-[#1A2A6C]" checked={recurring} onChange={(e) => setRecurring(e.target.checked)} data-testid="newride-recurring-toggle" />
                <span className="flex-1">
                  <span className="flex items-center gap-2 font-semibold text-[#1A1D29]">
                    <Repeat className="w-4 h-4 text-[#1A2A6C]" />
                    Ripeti ogni settimana
                  </span>
                  <span className="block text-sm text-[#8A91A8] mt-0.5">Crea automaticamente le corse nei giorni scelti, fino alla data indicata.</span>
                </span>
              </label>
              {recurring && (
                <div className="mt-4 space-y-4 anim-slide-up">
                  <div>
                    <label className="brut-label">Giorni della settimana</label>
                    <div className="flex flex-wrap gap-2">
                      {DOW_FULL.map((d, i) => {
                        const active = weekdays.includes(i);
                        return (
                          <button type="button" key={i} onClick={() => toggleWeekday(i)} className={`px-3.5 py-2 rounded-xl font-semibold text-sm transition-all ${active ? "bg-[#1A2A6C] text-white shadow-[0_4px_12px_rgba(26,42,108,0.32)]" : "bg-[#F5F6FA] text-[#1A1D29] hover:bg-[#E5E9F5]"}`} data-testid={`newride-weekday-${i}`}>{d}</button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="brut-label">Fino al</label>
                      <input type="date" className="brut-input" min={date} value={untilDate} onChange={(e) => setUntilDate(e.target.value)} required data-testid="newride-until-input" />
                    </div>
                    <div className="self-end">
                      <div className={`brut-chip ${previewCount === 0 || previewCount > 60 ? "bg-[#FFE3E3] text-[#FF4D5E]" : "bg-[#D4F1ED] text-[#26B5A8]"}`} data-testid="newride-preview-count">
                        {previewCount} {previewCount === 1 ? "corsa" : "corse"} create
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Map placeholder */}
          <div className="rounded-2xl border border-dashed border-[#C9D1E8] bg-[#F5F6FA] p-5 flex items-center gap-3">
            <div className="w-10 h-10 grid place-items-center rounded-xl bg-[#E5E9F5] text-[#1A2A6C] shrink-0">
              <MapPin className="w-5 h-5" />
            </div>
            <div className="text-sm">
              <p className="font-semibold text-[#1A1D29]">Mappa in arrivo</p>
              <p className="text-[#8A91A8]">Il calcolo del percorso più rapido sarà disponibile nella prossima versione.</p>
            </div>
          </div>

          {isOffer && mode === "car" && (
            <div>
              <label className="brut-label"><Users className="w-3 h-3 inline mr-1" /> Posti disponibili</label>
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                  <button type="button" key={n} onClick={() => setSeats(n)} className={`w-11 h-11 rounded-2xl font-heading font-bold text-lg transition-all ${seats === n ? "bg-[#1A2A6C] text-white shadow-[0_6px_18px_rgba(26,42,108,0.32)]" : "bg-white border border-[#ECEEF5] text-[#1A1D29] hover:bg-[#F5F6FA]"}`} data-testid={`newride-seats-${n}`}>{n}</button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="brut-label">Note (opzionale)</label>
            <textarea className="brut-input min-h-[90px] resize-none" placeholder={isOffer ? "Es. Passo davanti al parco alle 7:50…" : "Es. Posso aspettare 10 minuti, abito vicino…"} value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} data-testid="newride-notes-input" />
          </div>

          <div className="flex gap-3 pt-2">
            <Link to="/dashboard" className="brut-btn brut-btn-ghost" data-testid="newride-cancel">Annulla</Link>
            <button type="submit" className={`brut-btn flex-1 ${!isOffer ? "brut-btn-accent" : ""}`} disabled={loading || (isOffer && recurring && (previewCount === 0 || previewCount > 60))} data-testid="newride-submit">
              {loading ? "Invio…" : isOffer ? (recurring ? `Crea ${previewCount} corse` : "Crea corsa") : "Pubblica richiesta"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
